'use client';

// biome-ignore lint/performance/noNamespaceImport: library designed for namespace import
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useRef, useState } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { Link } from '@/app_components/common/Link';
import { FortuneReleaseModal, type FortuneReleaseModalRef } from '@/app_components/release-tools/FortuneReleaseModal';
import { useLongPress } from '@/app_lib/hooks/useLongPress';
import { unwrapLazy } from '@/app_lib/utils/sharedUtils';
import { cn } from '@/app_lib/utils/utils';
import { ConferencesExternalMenuItems, ConferencesInternalMenuItems } from '@/appconstants';

interface MenuItem {
  id: string;
  label: string;
  navigation: string;
  rel?: string;
  target?: string;
}

export const ConferencesDrawerMenu = ({ className }: { className?: string }) => {
  const [open, setOpen] = useState(false);
  const releaseModalRef = useRef<FortuneReleaseModalRef>(null);

  const longPressHandlers = useLongPress({
    duration: 500,
    onLongPress: () => {
      releaseModalRef.current?.openModal();
    },
  });

  const triggerOpen = (
    <div
      {...longPressHandlers}
      className={cn('-ml-[12px] -my-[20px] lg:my-0 lg:ml-0', className)}
      id='hamburgerButton'
    >
      <ActionableIcon className='hover:text-blue-1!'>
        <Icon
          aria-label='Open Navigation Menu'
          icon='Menu'
        />
      </ActionableIcon>
    </div>
  );

  const triggerClose = (
    <div
      className={cn('-ml-[12px] -my-[20px] lg:my-0 lg:ml-0', className)}
      id='hamburgerButton'
    >
      <ActionableIcon className='hover:text-blue-1!'>
        <Icon icon='X' />
      </ActionableIcon>
    </div>
  );

  const content = [...ConferencesInternalMenuItems, ...ConferencesExternalMenuItems];

  return (
    <>
      <DropdownMenu.Root
        modal={false}
        onOpenChange={setOpen}
        open={open}
      >
        <DropdownMenu.Trigger asChild>{open ? unwrapLazy(triggerClose) : unwrapLazy(triggerOpen)}</DropdownMenu.Trigger>
        <DropdownMenu.Content className='h-fit w-screen container-ft flex flex-col px-4 pt-1 md:pt-4 md:px-8 isolate z-header-nav'>
          <ul className='flex flex-col bg-pure rounded-b-[28px] px-xxs py-xs font-semibold text-xl'>
            {content.map((item: MenuItem) => (
              <DropdownMenu.Item
                asChild
                className='py-xs px-2 w-fit hover:underline hover:decoration-blue-1 hover:underline-offset-3 hover:decoration-2'
                key={item.id}
                onSelect={() => setOpen(false)}
                textValue={item.label}
              >
                <Link
                  className='group w-fit hover:underline hover:decoration-blue-1 hover:underline-offset-3 hover:decoration-2'
                  href={item.navigation}
                  key={item.id}
                  {...(item.rel && { rel: item.rel })}
                  {...(item.target && { target: item.target })}
                >
                  <span className='px-2 w-full whitespace-nowrap'>{item.label}</span>
                </Link>
              </DropdownMenu.Item>
            ))}
          </ul>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      <FortuneReleaseModal ref={releaseModalRef} />
    </>
  );
};
